import { Box } from "@mui/material";
import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import {
    // MeasureMenuToolbar,
    // MeasurePickerToolbar,
    PinsToolbar,
    QUICK_ACCESS_TOOLBAR,
    SettingsToolbar,
    ToolsMenuToolbar,
    ToolsPickerToolbar,
    UndoRedoToolbar,
    ViewToolbar,
} from "components/toolbars";
import { AxisSphere } from "features/axisSphere/AxisSphere";
import { useNonMarkerUIIsHidden } from "features/markers/markerCreationUtils";
import { measureActions, selectIsMenuOpen } from "features/measure";
import { Picker, selectIsSubtreesPanelOpen, selectPicker } from "features/render";
import { sectionPlaneActions, selectIsSectionMenuOpen } from "features/sectionPlane";
import { selectIsAxisSphereVisible, selectIsProcoreBeta } from "slices/explorer";

import { LaserSettings } from "./laserSettings";
import { PointToPointSettings } from "./pointToPointSettings";

const columnFlexStyles = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
} as const;

export function QuickAccessMenu() {
    const dispatch = useAppDispatch();
    const picker = useAppSelector(selectPicker);
    const isProcoreBeta = useAppSelector(selectIsProcoreBeta);
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible);
    const nonMarkerUIIsHidden = useNonMarkerUIIsHidden();
    const isMeasureMenuOpen = useAppSelector(selectIsMenuOpen);
    const isSectionMenuOpen = useAppSelector(selectIsSectionMenuOpen);
    const isSubtreesPanelOpen = useAppSelector(selectIsSubtreesPanelOpen);
    const hasOpenFlyout = isMeasureMenuOpen || isSectionMenuOpen || isSubtreesPanelOpen;

    // Close flyout menus when hiding UI so MUI Popper anchorEl stays in document layout
    useEffect(() => {
        if (nonMarkerUIIsHidden) {
            dispatch(measureActions.setMenuOpen(false));
            dispatch(sectionPlaneActions.setMenuOpen(false));
        }
    }, [dispatch, nonMarkerUIIsHidden]);

    if (!isProcoreBeta) {
        return null;
    }

    return (
        <Box
            sx={{
                position: "absolute",
                right: "8px",
                top: "8px",
                pointerEvents: "none",
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "start",
                gap: "8px",
            }}
        >
            <Box
                sx={{
                    ...columnFlexStyles,
                    zIndex: hasOpenFlyout ? QUICK_ACCESS_TOOLBAR.FLYOUT_Z_INDEX : undefined,
                }}
            >
                <PinsToolbar picker={picker} id="pins-toolbar-close" />
                <Box
                    sx={{
                        visibility: nonMarkerUIIsHidden ? "hidden" : undefined,
                        pointerEvents: nonMarkerUIIsHidden ? "none" : undefined,
                    }}
                >
                    <ViewToolbar id="quick-access-camera" />
                </Box>
                <Box
                    sx={{
                        ...columnFlexStyles,
                        visibility: nonMarkerUIIsHidden ? "hidden" : undefined,
                        pointerEvents: nonMarkerUIIsHidden ? "none" : undefined,
                    }}
                >
                    <ToolsPickerToolbar picker={picker} id="tools-toolbar-close" />
                    <ToolsMenuToolbar picker={picker} id="quick-access-tools-menu" />
                    <UndoRedoToolbar id="quick-access-undo-redo" />
                    <SettingsToolbar id="quick-access-settings" />
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    alignItems: "flex-end",
                }}
            >
                <Box
                    sx={{
                        ...columnFlexStyles,
                        alignItems: "flex-end",
                        visibility: nonMarkerUIIsHidden ? "hidden" : undefined,
                        pointerEvents: nonMarkerUIIsHidden ? "none" : undefined,
                    }}
                >
                    {picker === Picker.OutlineLaser && <LaserSettings />}
                    {picker === Picker.PointLine && <PointToPointSettings />}
                </Box>
                {isAxisSphereVisible && <AxisSphere />}
            </Box>
        </Box>
    );
}
